import { type FC, type ReactNode } from 'react';

// MUI
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CardActionArea from '@mui/material/CardActionArea';
import Skeleton from '@mui/material/Skeleton';

import KiosImage from 'modules/quick-search/components/KiosImage';

interface Props {
  image?: string | null;
  title: string;
  description?: string | null;
  /** Nhãn số lượng cấp con (vd "(6) NMH" ở PLHH, "(5) HSNL NCC" ở NMH). */
  countLabel?: ReactNode;
  selected?: boolean;
  onClick: () => void;
}

/** Card lưới cho 1 mục PLHH/NMH: ảnh trên + tiêu đề + số lượng cấp con + mô tả. */
const CategoryCard: FC<Props> = ({
  image,
  title,
  description,
  countLabel,
  selected = false,
  onClick,
}) => {
  return (
    <CardActionArea
      onClick={onClick}
      sx={{
        borderRadius: 3,
        overflow: 'hidden',
        bgcolor: 'background.paper',
        boxShadow: 'none',
        border: '1px solid',
        borderColor: selected ? 'primary.main' : 'divider',
        transition: 'border-color .2s, transform .2s',
        '&:hover': { borderColor: 'primary.main' },
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
      }}
    >
      <Box
        sx={{
          height: 200,
          bgcolor: 'action.hover',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <KiosImage
          src={image}
          alt={title}
          fallbackIconSize={48}
          sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
        />
      </Box>

      <Box sx={{ p: 2, flexGrow: 1 }}>
        <Typography sx={{ fontWeight: 700, color: 'text.primary', mb: 0.5 }}>{title}</Typography>
        {countLabel != null && (
          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}>
            {countLabel}
          </Typography>
        )}
        {description ? (
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {description}
          </Typography>
        ) : null}
      </Box>
    </CardActionArea>
  );
};

/** Khung chờ cùng kích thước CategoryCard để lưới không nhảy khi dữ liệu về. */
export const CategoryCardSkeleton: FC = () => (
  <Box
    sx={{
      borderRadius: 3,
      overflow: 'hidden',
      border: '1px solid',
      borderColor: 'divider',
      bgcolor: 'background.paper',
    }}
  >
    <Skeleton variant="rectangular" height={200} />
    <Box sx={{ p: 2 }}>
      <Skeleton width="60%" height={24} />
      <Skeleton width="30%" height={16} />
    </Box>
  </Box>
);

export default CategoryCard;
